import React, { useMemo, forwardRef, useEffect } from 'react';
import closeOnClickOutside from './../core/close-on-click-outside';
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import { IconRocket, IconSpaces } from '@tabler/icons-react';

const dict = {
    en: {
        workspaceLabel: 'WorkSpace',
        yourWorkspaces: 'Your WorkSpaces',
        manageWorkspaces: 'Manage WorkSpaces',
        team: 'team',
        user_one: 'user',
        user_many: 'users',
        currentBadge: 'Active',
        currentLabel: 'Current workspace',
    },
    ru: {
        workspaceLabel: 'Рабочая область',
        yourWorkspaces: 'Ваши рабочие области',
        manageWorkspaces: 'Управление рабочими областями',
        team: 'команда',
        user_one: 'пользователь',
        user_few: 'пользователя',
        user_many: 'пользователей',
        currentBadge: 'Активная',
        currentLabel: 'Текущая область',
    },
    ua: {
        workspaceLabel: 'Робочий простір',
        yourWorkspaces: 'Ваші робочі простори',
        manageWorkspaces: 'Керування робочими просторами',
        team: 'команда',
        user_one: 'користувач',
        user_few: 'користувачі',
        user_many: 'користувачів',
        currentBadge: 'Активний',
        currentLabel: 'Поточний простір',
    },
};

// RU/UA pluralization helper (one/few/many). EN handled separately.
const plural = (n, forms) => {
    const n10 = n % 10, n100 = n % 100;
    if (n10 === 1 && n100 !== 11) return forms.one;
    if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return forms.few;
    return forms.many;
};

const usersLabel = (lang, n, t) => {
    if (lang === 'en') return n === 1 ? t.user_one : t.user_many;
    if (lang === 'ru') return plural(n, { one: t.user_one, few: t.user_few, many: t.user_many });
    // ua
    return plural(n, { one: t.user_one, few: t.user_few, many: t.user_many });
};

const MenuWorkSpaces = forwardRef(({ open, setOpen, lang = 'en' }, ref) => {
    const navigate = useNavigate();
    const { user, selectWorkspace } = useAuth();
    const t = useMemo(() => dict[lang] || dict.en, [lang]);
    const openClass = open ? "show" : "";

    useEffect(() => {
        // Например, если нужно реагировать на смену пользователя/раб. пространства
        // console.log(" - user details changed - ")
    }, [user]);

    const workspaces = user?.workspaces || [];
    const currentWorkspaceId = user?.company_id || user?.workspace_id || user?.current_workspace_id;
    const normalizeId = (workspace) => workspace?.company_id ?? workspace?.workspace_id ?? workspace?.id;

    return (
        <>
            <a
                className="nav-link"
                href="javascript:void(0)"
                onClick={() => setOpen(!open)}
            >
                {t.workspaceLabel}:&nbsp;<b>{user?.work_section}</b>
                <span className="mt-1"><i className="ti ti-chevron-down fs-3"></i></span>
            </a>

            <div
                className={`dropdown-menu content-dd dropdown-menu-end dropdown-menu-animate-up ${openClass}`}
                aria-labelledby="drop1"
                data-bs-popper="static"
                ref={ref}
            >
                <div className="profile-dropdown position-relative" data-simplebar="init">
                    <div className="simplebar-wrapper" style={{ margin: 0 }}>
                        <div className="simplebar-height-auto-observer-wrapper">
                            <div className="simplebar-height-auto-observer" />
                        </div>
                        <div className="simplebar-mask">
                            <div className="simplebar-offset" style={{ right: 0, bottom: 0 }}>
                                <div
                                    className="simplebar-content-wrapper"
                                    tabIndex="0"
                                    role="region"
                                    aria-label="scrollable content"
                                    style={{ height: "auto", overflow: "hidden" }}
                                >
                                    <div className="simplebar-content" style={{ padding: 0 }}>
                                        <div className="py-3 px-7 pb-0">
                                            <h5 className="mb-0 fs-5 fw-semibold">{t.yourWorkspaces}</h5>
                                        </div>

                                        <div className="d-flex align-items-center py-9 border-bottom" style={{ paddingLeft: 20 }}>
                                            <Link
                                                to="/workspaces"
                                                className="py-8 px-7 mt-8 d-flex align-items-center"
                                                onClick={() => setOpen(false)}
                                            >
                        <span className="d-flex align-items-center justify-content-center text-bg-light rounded-1 p-6">
                          <IconRocket size={24} />
                        </span>
                                                <div className="w-75 d-inline-block v-middle ps-3">
                                                    <h6 className="mb-1 fs-3 fw-semibold lh-base">{t.manageWorkspaces}</h6>
                                                </div>
                                            </Link>
                                        </div>

                                        <div className="message-body">
                                            {workspaces.map((workspace) => {
                                                const count = Number(workspace.users_count ?? 0);
                                                const isActive = currentWorkspaceId && String(normalizeId(workspace)) === String(currentWorkspaceId);
                                                return (
                                                    <a
                                                        key={workspace.company_id}
                                                        href="javascript:void(0)"
                                                        className="px-7 mt-8 d-flex align-items-center"
                                                        onClick={async () => {
                                                            setOpen(false);
                                                            await selectWorkspace(workspace.company_id);
                                                            navigate('/dashboard');
                                                        }}
                                                    >
                            <span className="d-flex align-items-center justify-content-center text-bg-light rounded-1 p-3">
                              <IconSpaces size={24} />
                            </span>

                                                        <div className="w-75 d-inline-block v-middle ps-3">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <h6 className="mb-1 fs-3 fw-semibold lh-base">
                                                                    {workspace.company_name}
                                                                </h6>
                                                                {isActive && (
                                                                    <span className="badge bg-primary">
                                                                        {t.currentBadge}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <span className="fs-2 d-block text-body-secondary">
                                <b>{t.team}</b>: {count} {usersLabel(lang, count, t)}
                              </span>
                                                        </div>
                                                    </a>
                                                );
                                            })}
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="simplebar-placeholder" style={{ width: 360, height: 603 }} />
                    </div>

                    <div className="simplebar-track simplebar-horizontal" style={{ visibility: "hidden" }}>
                        <div className="simplebar-scrollbar" style={{ width: 0, display: "none" }} />
                    </div>
                    <div className="simplebar-track simplebar-vertical" style={{ visibility: "hidden" }}>
                        <div className="simplebar-scrollbar" style={{ height: 0, display: "none" }} />
                    </div>
                </div>
            </div>
        </>
    );
});

export default closeOnClickOutside(MenuWorkSpaces);
